import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { agentService } from '../../services/agentService';
import { composeSystemPromptFromSections, normaliseSections } from '../../utils/promptUtils';
import {
    ReactFlow,
    MiniMap,
    Controls,
    Background,
    BackgroundVariant,
    addEdge,
    applyNodeChanges,
    applyEdgeChanges,
    type Node,
    type Edge,
    type Connection,
    type NodeChange,
    type EdgeChange,
    useReactFlow,
    ConnectionMode,
    MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
    McButton,
    McIcon,
    McTag,
    McModal,
    McNotification
} from '@maersk-global/mds-react-wrapper';
import { useMutation } from '@tanstack/react-query';
import FloatingAssistant from '../FloatingAssistant';
import { agentNodeTypes } from './nodes/AgentNodes';
import { NODE_CONFIGS, AgentNodeType } from './nodes/nodeTypes';
import NodePalette from './NodePalette';
import NodeConfigPanel from './NodeConfigPanel';
import ActivityLogsPanel from './ActivityLogsPanel';
import { ConfirmationDialog } from '../Common/ConfirmationDialog';
import { McTooltip } from '@maersk-global/mds-react-wrapper/components-core/mc-tooltip';
import { defaultTerminalOption, DatabaseType } from '../../utils/terminalConfig';
import { inferAgentTestChatProfile } from '../../utils/agentTestChatProfile';
import { VALID_MODEL_VALUES, DEFAULT_MODEL } from '../../config/modelRegistry';
import './AgentBuilder.css';

interface AgentBuilderProps {
    agentId?: string;
    initialName?: string;
    initialNodes?: Node[];
    initialEdges?: Edge[];
    initialStatus?: string;
    onBack: () => void;
    readOnly?: boolean;
}

let nodeIdCounter = 0;
const getNewId = () => `node_${Date.now()}_${nodeIdCounter++}`;

const AgentBuilder: React.FC<AgentBuilderProps> = ({
    agentId,
    initialName = 'Untitled Agent',
    initialNodes = [],
    initialEdges = [],
    initialStatus = 'DRAFT',
    onBack,
    readOnly = false,
}) => {
    const [nodes, setNodes] = useState<Node[]>(initialNodes);
    const [edges, setEdges] = useState<Edge[]>(initialEdges);
    const [agentName, setAgentName] = useState(initialName);
    const [selectedNode, setSelectedNode] = useState<Node | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [currentAgentId, setCurrentAgentId] = useState<string | undefined>(agentId);
    const [agentStatus, setAgentStatus] = useState<string>(initialStatus);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [confirmPublishOpen, setConfirmPublishOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<'builder' | 'logs'>('builder');
    const [isSaving, setIsSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);

    // Test in Chat states
    const MAX_CHAT_HISTORY = 20; // sliding window: oldest messages dropped first
    const [isTestChatOpen, setIsTestChatOpen] = useState(false);
    const [testChatHistory, setTestChatHistory] = useState<any[]>([]);
    const [testSessionId, setTestSessionId] = useState(() => crypto.randomUUID());
    const [testResponseType, setTestResponseType] = useState<'PLAINTEXT' | 'MARKDOWN' | 'JSON'>('MARKDOWN');
    const appendChatMessage = (msg: { role: string; content: string }) => {
        setTestChatHistory((prev) => {
            const next = [...prev, msg];
            return next.length > MAX_CHAT_HISTORY ? next.slice(next.length - MAX_CHAT_HISTORY) : next;
        });
    };
    const [testAiIntent, setTestAiIntent] = useState('');
    const [selectedTerminal, setSelectedTerminal] = useState('');
    const [databaseType, setDatabaseType] = useState<DatabaseType>('ORACLE');
    const chatScrollRef = useRef<HTMLDivElement>(null);
    const reactFlowWrapper = useRef<HTMLDivElement>(null);
    const rfInstance = useReactFlow();

    // executeSave is invoked from async paths (Apply tool config, Test in Chat). Those closures can be one render
    // behind; always read the latest graph from refs so persisted workflow matches the canvas.
    const nodesRef = useRef(nodes);
    const edgesRef = useRef(edges);
    nodesRef.current = nodes;
    edgesRef.current = edges;

    // Stable node types reference
    const nodeTypes = useMemo(() => agentNodeTypes, []);

    const testChatProfile = useMemo(() => inferAgentTestChatProfile(nodes, agentName), [nodes, agentName]);

    // Keep identity/status in sync when parent switches the selected agent.
    useEffect(() => {
        setCurrentAgentId(agentId);
        setAgentStatus(initialStatus);
        setAgentName(initialName);
    }, [agentId, initialStatus, initialName]);

    useEffect(() => {
        let cancelled = false;

        // Migrate stale model values (e.g. removed 'gpt-4o') to a valid default
        const migrateModelValues = (nodeList: Node[]): Node[] =>
            nodeList.map((node) => {
                if (node.type !== 'aiAction') return node;
                const nd = (node.data || {}) as Record<string, any>;
                if (nd.model && !VALID_MODEL_VALUES.includes(nd.model)) {
                    return { ...node, data: { ...nd, model: DEFAULT_MODEL.value } };
                }
                return node;
            });

        const hydratePromptTemplateNodes = async () => {
            const aiActionNodes = initialNodes.filter(
                (node) => node.type === 'aiAction' && typeof (node.data as Record<string, any>)?.promptTemplateId === 'string'
            );

            if (aiActionNodes.length === 0) {
                setNodes(migrateModelValues(initialNodes));
                return;
            }

            try {
                const templates = await agentService.getPromptTemplates();
                if (cancelled) {
                    return;
                }

                const templateMap = new Map(
                    templates.map((template) => [template.templateId, template])
                );

                const hydratedNodes = initialNodes.map((node) => {
                    if (node.type !== 'aiAction') {
                        return node;
                    }

                    const nodeData = (node.data || {}) as Record<string, any>;
                    const promptTemplateId = typeof nodeData.promptTemplateId === 'string'
                        ? nodeData.promptTemplateId
                        : '';

                    if (!promptTemplateId) {
                        return node;
                    }

                    const template = templateMap.get(promptTemplateId);
                    if (!template) {
                        return node;
                    }

                    const promptSections = template.contentSections
                        && Object.keys(template.contentSections).length > 0
                        ? normaliseSections(template.contentSections)
                        : normaliseSections({ roleContext: template.content || '' });

                    return {
                        ...node,
                        data: {
                            ...nodeData,
                            promptTemplateId,
                            promptSections,
                            prompt: composeSystemPromptFromSections(promptSections),
                            databaseType: template.databaseType || nodeData.databaseType,
                        },
                    };
                });

                setNodes(migrateModelValues(hydratedNodes));
            } catch {
                if (!cancelled) {
                    setNodes(migrateModelValues(initialNodes));
                }
            }
        };

        hydratePromptTemplateNodes();

        return () => {
            cancelled = true;
        };
    }, [initialNodes]);

    const onNodesChange = useCallback(
        (changes: NodeChange[]) => {
            setNodes((nds) => {
                const updated = applyNodeChanges(changes, nds);
                nodesRef.current = updated;
                return updated;
            });
            // Only mark as unsaved if it's a structural or positional change, not just selection
            const hasDraftChanges = changes.some(c => c.type !== 'select');
            if (hasDraftChanges) {
                setHasUnsavedChanges(true);
            }
            // Update selected node reference if it was changed
            const selectionChange = changes.find(
                (c) => c.type === 'select' && (c as any).selected
            );
        },
        []
    );

    const onEdgesChange = useCallback(
        (changes: EdgeChange[]) => {
            setEdges((eds) => applyEdgeChanges(changes, eds));
            const hasDraftChanges = changes.some(c => c.type !== 'select');
            if (hasDraftChanges) {
                setHasUnsavedChanges(true);
            }
        },
        []
    );

    const onConnect = useCallback(
        (params: Connection) => {
            const isToolConnection = params.sourceHandle === 'tools';

            setEdges((eds) =>
                addEdge(
                    {
                        ...params,
                        animated: true,
                        style: isToolConnection
                            ? { strokeWidth: 2, stroke: '#4B5563', strokeDasharray: '5,5' }
                            : { strokeWidth: 2.5, stroke: '#3B82F6' }, // Blue for execution flow
                        markerEnd: {
                            type: MarkerType.ArrowClosed,
                            width: 20,
                            height: 20,
                            color: isToolConnection ? '#4B5563' : '#3B82F6',
                        },
                        label: isToolConnection ? '🛠️ tool' : undefined,
                        labelStyle: { fill: '#4B5563', fontWeight: 700, fontSize: 12 },
                        labelBgPadding: [8, 4],
                        labelBgBorderRadius: 4,
                        labelBgStyle: { fill: '#F3F4F6', fillOpacity: 0.9 },
                    },
                    eds
                )
            );
        },
        [nodes, readOnly]
    );

    const onNodeClick = useCallback(
        (_event: React.MouseEvent, node: Node) => {
            setSelectedNode(node);
        },
        []
    );

    const onPaneClick = useCallback(() => {
        setSelectedNode(null);
    }, []);

    // Drag and drop from palette
    const onDragOver = useCallback((event: React.DragEvent) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setIsDragOver(true);
    }, []);

    const onDragLeave = useCallback(() => {
        setIsDragOver(false);
    }, []);

    const onDrop = useCallback(
        (event: React.DragEvent) => {
            event.preventDefault();
            if (readOnly) return;
            setIsDragOver(false);

            const nodeType = event.dataTransfer.getData('application/agentNodeType') as AgentNodeType;
            if (!nodeType || !NODE_CONFIGS[nodeType]) return;

            const config = NODE_CONFIGS[nodeType];
            const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
            if (!reactFlowBounds || !rfInstance) return;

            const position = rfInstance.screenToFlowPosition({
                x: event.clientX - reactFlowBounds.left,
                y: event.clientY - reactFlowBounds.top,
            });

            const nodeDataStr = event.dataTransfer.getData('application/agentNodeData');
            const initialData = nodeDataStr ? JSON.parse(nodeDataStr) : {};

            const newNode: Node = {
                id: getNewId(),
                type: nodeType,
                position,
                data: { ...config.defaultData, ...initialData },
            };

            setNodes((nds) => [...nds, newNode]);
            setHasUnsavedChanges(true);
        },
        [rfInstance, readOnly]
    );

    const handleDragStart = useCallback(
        (event: React.DragEvent, nodeType: AgentNodeType, initialData?: any) => {
            event.dataTransfer.setData('application/agentNodeType', nodeType);
            if (initialData) {
                event.dataTransfer.setData('application/agentNodeData', JSON.stringify(initialData));
            }
            event.dataTransfer.effectAllowed = 'move';
        },
        []
    );

    const handleNodeUpdate = useCallback(
        (nodeId: string, newData: Record<string, any>) => {
            setNodes((nds) => {
                const updated = nds.map((n) => (n.id === nodeId ? { ...n, data: newData } : n));
                // Eagerly sync the ref inside the updater so that async callers
                // (Apply tool config → onPersistWorkflow → executeSave) always
                // read the latest graph — even before React re-renders.
                nodesRef.current = updated;
                return updated;
            });
            if (!readOnly) setHasUnsavedChanges(true);
            // Also update selected node state
            setSelectedNode((prev) =>
                prev && prev.id === nodeId ? { ...prev, data: newData } : prev
            );
        },
        []
    );

    const handleNodeDelete = useCallback(
        (nodeId: string) => {
            setNodes((nds) => nds.filter((n) => n.id !== nodeId));
            setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
            setSelectedNode(null);
            if (!readOnly) setHasUnsavedChanges(true);
        },
        [readOnly]
    );

    const executeSave = async (): Promise<string> => {
        if (!rfInstance) {
            setErrorMessage("Warning: rfInstance is null, cannot save.");
            throw new Error("rfInstance is null");
        }
        try {
            // Use refs so async callers (Apply → onPersistWorkflow, Test in Chat) do not serialize a stale graph.
            const currentNodes = nodesRef.current;
            const currentEdges = edgesRef.current;
            const viewport = rfInstance.getViewport();

            const triggerNode = currentNodes.find((n: any) => n.type === 'trigger');
            const aiActionNode = currentNodes.find((n: any) => n.type === 'aiAction');
            const aiActionData = (aiActionNode?.data || {}) as Record<string, any>;
            const composedPrompt = composeSystemPromptFromSections(aiActionData.promptSections);

            if (aiActionData.promptTemplateId && aiActionData.promptSections) {
                await agentService.updatePromptTemplate(String(aiActionData.promptTemplateId), {
                    contentSections: aiActionData.promptSections,
                    content: composedPrompt,
                    databaseType: aiActionData.databaseType ? String(aiActionData.databaseType) : undefined,
                });
            }

            // Extract tool configs from tool nodes into a separate map, keyed by nodeId.
            // This keeps secrets out of the workflow object.
            const toolConfigs: Record<string, Record<string, any>> = {};
            const cleanNodes = currentNodes.map((node: any) => {
                if (node.type !== 'tool' || !node.data?.toolsConfig) return node;
                const toolId = node.data.toolId || '';
                const cfg = node.data.toolsConfig[toolId];
                if (cfg && toolId) {
                    toolConfigs[node.id] = { ...cfg, toolId };
                }
                // Strip toolsConfig from the node so it's not in the workflow
                const { toolsConfig, ...restData } = node.data;
                return { ...node, data: restData };
            });

            const workflow = {
                nodes: cleanNodes,
                edges: currentEdges,
                viewport,
            };

            const payload: Record<string, any> = {
                name: agentName,
                description: aiActionData.description
                    ? String(aiActionData.description)
                    : undefined,
                workflow,
                toolConfigs,
                triggerType: String(triggerNode?.data?.triggerType || 'manual'),
                cronExpression: triggerNode?.data?.cronExpression
                    ? String(triggerNode.data.cronExpression)
                    : undefined,
                // systemPrompt is NOT sent — the backend composes it server-side
                // from the promptSections stored in the workflow's aiAction node data.
                // This prevents prompt injection via the API.
                // promptTemplateId is derived by the backend from the workflow's aiAction
                // node data, so we send it only as a hint (null when Custom mode).
                promptTemplateId: aiActionData.promptTemplateId
                    ? String(aiActionData.promptTemplateId)
                    : null,
            };

            let savedAgentId = currentAgentId;
            if (currentAgentId && currentAgentId.includes('-')) {
                await agentService.update(currentAgentId, payload);
            } else {
                const created = await agentService.create(payload);
                setCurrentAgentId(created.agentId);
                setAgentStatus(created.status || 'DRAFT');
                savedAgentId = created.agentId;
            }
            setHasUnsavedChanges(false);
            return savedAgentId || '';
        } catch (err: any) {
            throw err;
        }
    };

    const handleSave = useCallback(async () => {
        setIsSaving(true);
        setSaveSuccess(false);
        try {
            await executeSave();
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 2000);
        } catch (err: any) {
            setErrorMessage(`Save failed: ${err.message}`);
        } finally {
            setIsSaving(false);
        }
    }, [rfInstance, currentAgentId, agentName, nodes, edges]);

    const handlePublishClick = useCallback(() => {
        setConfirmPublishOpen(true);
    }, []);

    const executePublish = useCallback(async () => {
        setConfirmPublishOpen(false);
        let publishAgentId = currentAgentId;

        try {
            if (hasUnsavedChanges || !publishAgentId || !publishAgentId.includes('-')) {
                publishAgentId = await executeSave();
            }
            const deployed = await agentService.deploy(publishAgentId!);
            setCurrentAgentId(deployed.agentId || publishAgentId);
            setAgentStatus(deployed.status || 'ACTIVE');
            setHasUnsavedChanges(false);
            setSuccessMessage(`Agent "${agentName}" published successfully!`);
        } catch (err: any) {
            setErrorMessage(`Publish failed: ${err.message}`);
        }
    }, [currentAgentId, agentName, hasUnsavedChanges, rfInstance]);

    const handleExport = useCallback(() => {
        if (!rfInstance) return;
        const flow = rfInstance.toObject();
        const data = JSON.stringify({ name: agentName, ...flow }, null, 2);
        const blob = new Blob([data], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${agentName.replace(/\s+/g, '_').toLowerCase()}.agent.json`;
        a.click();
        URL.revokeObjectURL(url);
    }, [rfInstance, agentName]);

    // Execute loads workflow from DB only — keep refs so Test in Chat always persists the latest canvas first.
    const executeSaveRef = useRef(executeSave);
    const currentAgentIdRef = useRef(currentAgentId);
    const agentStatusRef = useRef(agentStatus);
    const readOnlyRef = useRef(readOnly);
    executeSaveRef.current = executeSave;
    currentAgentIdRef.current = currentAgentId;
    agentStatusRef.current = agentStatus;
    readOnlyRef.current = readOnly;

    const ensureExecutableAgentId = useCallback(async (): Promise<string> => {
        const savedId = await executeSaveRef.current();
        const resolvedId = savedId || currentAgentIdRef.current;
        if (!resolvedId?.includes('-')) {
            throw new Error('Save the agent before testing');
        }

        // Always verify the real status from the backend to avoid stale local state.
        // The local agentStatusRef can be out of sync after deploys, saves, or re-renders.
        const freshAgent = await agentService.get(resolvedId);
        const backendStatus = freshAgent.status || 'DRAFT';

        if (backendStatus !== 'ACTIVE' && backendStatus !== 'TESTING') {
            const tested = await agentService.test(resolvedId);
            setCurrentAgentId(tested.agentId || resolvedId);
            setAgentStatus(tested.status || 'TESTING');
        } else {
            setAgentStatus(backendStatus);
        }
        return resolvedId;
    }, []);

    // Test in Chat Logic
    const aiTestMutation = useMutation({
        mutationFn: async (vars: { message: string; history: any[] }) => {
            const requestId = crypto.randomUUID();
            const requestOptions = {
                requestId,
                responseType: testResponseType,
                stream: false,
            };

            if (readOnlyRef.current) {
                const id = currentAgentIdRef.current;
                if (!id?.includes('-')) throw new Error('Agent must be saved before testing');
                return agentService.execute(id, vars.message, 'web_ui', undefined, testSessionId, undefined, requestOptions);
            }
            // Persist latest workflow and auto-promote to TESTING when still DRAFT.
            const id = await ensureExecutableAgentId();
            return agentService.execute(id, vars.message, 'web_ui', undefined, testSessionId, undefined, requestOptions);
        },
        onSuccess: (response: any) => {
            appendChatMessage({ role: 'assistant', content: response.response });
        },
        onError: (error: any) => {
            const respData = error?.response?.data;
            const detail = respData?.detail || respData?.title || error?.message;
            const status = error?.response?.status;
            const resetMs = respData?.windowResetEpochMs;
            const resetInfo = resetMs
                ? ` You can try again after ${new Date(resetMs).toLocaleString()}.`
                : '';
            const msg = status === 429
                ? `You have reached your AI usage limit for this quota period.${resetInfo}`
                : `Error: ${detail}`;
            appendChatMessage({ role: 'assistant', content: msg });
        },
    });

    const handleAiTestSendMessage = () => {
        const intent = testAiIntent.trim();
        if (!intent) return;
        if (readOnly && !currentAgentId?.includes('-')) return;

        appendChatMessage({ role: 'user', content: intent });
        setTestAiIntent('');

        aiTestMutation.mutate({ message: intent, history: testChatHistory });
    };

    const handleAiTestRetryMessage = (message: string) => {
        const retryIntent = (message || '').trim();
        if (!retryIntent) return;
        if (readOnly && !currentAgentId?.includes('-')) return;
        if (aiTestMutation.isPending) return;

        appendChatMessage({ role: 'user', content: retryIntent });
        setTestAiIntent('');
        aiTestMutation.mutate({ message: retryIntent, history: testChatHistory });
    };

    const handleNewTestChat = useCallback(() => {
        setTestChatHistory([]);
        setTestAiIntent('');
        setTestSessionId(crypto.randomUUID());
    }, []);

    return (
        <div className="agent-builder">
            {/* Toolbar */}
            <div className="agent-builder__toolbar">
                <div className="agent-builder__toolbar-left">
                    <McButton variant="outlined" appearance="neutral" onClick={onBack}>
                        <McIcon icon="arrow-left" slot="prefix" />
                        Back
                    </McButton>
                    <input
                        className="agent-builder__name"
                        value={agentName}
                        onChange={(e) => {
                            setAgentName(e.target.value);
                            setHasUnsavedChanges(true);
                        }}
                        placeholder="Agent name..."
                    />
                    <McTag fit="small" appearance={agentStatus === 'ACTIVE' ? 'success' : agentStatus === 'TESTING' ? 'warning' : 'neutral'}>
                        {agentStatus === 'ACTIVE'
                            ? hasUnsavedChanges ? 'Published · Unsaved changes' : 'Published'
                            : agentStatus === 'TESTING'
                                ? hasUnsavedChanges ? 'Testing · Unsaved changes' : 'Testing'
                                : hasUnsavedChanges ? 'Draft · Unsaved changes' : 'Draft'}
                    </McTag>
                </div>
                <div className="agent-builder__toolbar-right">
                    <div className="agent-builder__view-toggle" style={{ display: 'flex', gap: '4px', marginRight: '16px', background: '#F3F4F6', padding: '4px', borderRadius: '8px' }}>
                        <McButton
                            variant="outlined"
                            appearance={viewMode === 'builder' ? 'primary' : 'neutral'}
                            onClick={() => setViewMode('builder')}
                            style={{ background: viewMode === 'builder' ? 'white' : 'transparent', boxShadow: viewMode === 'builder' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}
                        >
                            <McIcon icon="database" slot="prefix" />
                            Builder
                        </McButton>
                        <McButton
                            variant="outlined"
                            appearance={viewMode === 'logs' ? 'primary' : 'neutral'}
                            onClick={() => setViewMode('logs')}
                            style={{ background: viewMode === 'logs' ? 'white' : 'transparent', boxShadow: viewMode === 'logs' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}
                        >
                            <McIcon icon="list-bullets" slot="prefix" />
                            Activity Logs
                        </McButton>
                    </div>
                    <McButton variant="outlined" appearance="neutral" onClick={handleExport}>
                        <McIcon icon="tray-arrow-down" slot="prefix" />
                        Export JSON
                    </McButton>
                    {!readOnly && (
                        <McButton
                            variant="outlined"
                            appearance="neutral"
                            onClick={handleSave}
                            disabled={isSaving || !hasUnsavedChanges}
                        >
                            <McIcon icon={saveSuccess ? 'check' : 'floppy-disk'} slot="prefix" />
                            {isSaving ? 'Saving\u2026' : saveSuccess ? 'Saved \u2713' : 'Save'}
                        </McButton>
                    )}
                    <McButton variant="outlined" appearance="primary" onClick={() => setIsTestChatOpen(true)}>
                        <McIcon icon="comment-dots" slot="prefix" />
                        Try in Chat
                    </McButton>
                    {!readOnly && (
                        <McButton appearance="primary" onClick={handlePublishClick}>
                            <McIcon icon="play" slot="prefix" />
                            Publish
                        </McButton>
                    )}
                </div>
            </div>

            <McNotification
                appearance="info"
                heading="Getting Started"
                style={{ margin: '0 24px 16px 24px' }}
            >
                Connect the Trigger → AI Action → Tool nodes to define your agent's capabilities. Save your work, then use Try in Chat to verify behavior before publishing.
            </McNotification>

            {/* Workspace */}
            <div className="agent-builder__workspace">
                {viewMode === 'logs' ? (
                    <ActivityLogsPanel agentId={currentAgentId || ''} />
                ) : (
                    <>
                        {/* Left: component palette */}
                        {!readOnly && <NodePalette onDragStart={handleDragStart} />}

                        {/* Center: canvas */}
                        <div
                            className={`agent-builder__canvas ${isDragOver ? 'agent-builder__canvas--drag-over' : ''}`}
                            ref={reactFlowWrapper}
                        >
                            {nodes.length === 0 && (
                                <div className="agent-builder__empty">
                                    <div className="agent-builder__empty-icon">🧩</div>
                                    <p className="agent-builder__empty-title">Start building your agent</p>
                                    <p className="agent-builder__empty-subtitle">
                                        Drag components from the left panel to get started
                                    </p>
                                </div>
                            )}

                            <ReactFlow
                                nodes={nodes}
                                edges={edges}
                                onNodesChange={onNodesChange}
                                onEdgesChange={onEdgesChange}
                                onConnect={onConnect}
                                onNodeClick={onNodeClick}
                                onPaneClick={onPaneClick}
                                onDragOver={onDragOver}
                                onDragLeave={onDragLeave}
                                onDrop={onDrop}
                                nodeTypes={nodeTypes}
                                connectionMode={ConnectionMode.Loose}
                                isValidConnection={() => true}
                                fitView
                                snapToGrid
                                snapGrid={[16, 16]}
                                connectionRadius={30}
                                deleteKeyCode={readOnly ? null : ['Backspace', 'Delete']}
                                nodesDraggable={!readOnly}
                                nodesConnectable={!readOnly}
                                elementsSelectable={true}
                                defaultEdgeOptions={{
                                    animated: true,
                                    style: { strokeWidth: 2.5, stroke: '#9CA3AF' },
                                }}
                            >
                                <Controls position="bottom-left" />
                                <MiniMap
                                    position="bottom-right"
                                    nodeStrokeWidth={3}
                                    nodeColor={(n) => {
                                        const config = NODE_CONFIGS[n.type as AgentNodeType];
                                        return config ? config.color : '#9CA3AF';
                                    }}
                                    style={{ width: 150, height: 100 }}
                                />
                                <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#D1D5DB" />
                            </ReactFlow>
                        </div>

                        {/* Right: config panel (shown when node is selected) */}
                        {selectedNode && (
                            <NodeConfigPanel
                                node={selectedNode}
                                nodes={nodes}
                                edges={edges}
                                onUpdate={handleNodeUpdate}
                                onClose={() => setSelectedNode(null)}
                                onDelete={handleNodeDelete}
                                readOnly={readOnly}
                                onPersistWorkflow={
                                    readOnly
                                        ? undefined
                                        : async () => {
                                              await executeSave();
                                          }
                                }
                            />
                        )}
                    </>
                )}
            </div>

            {/* Dialogs */}
            <ConfirmationDialog
                open={confirmPublishOpen}
                onClose={() => setConfirmPublishOpen(false)}
                onConfirm={executePublish}
                title="Publish Agent"
                content={
                    <div>
                        <p>This will make <strong>{agentName}</strong> available to users. Continue?</p>
                        {hasUnsavedChanges && (
                            <p style={{ marginTop: '8px', color: '#B45309' }}>
                                <strong>Note:</strong> Unsaved changes will be saved automatically before publishing.
                            </p>
                        )}
                    </div>
                }
                confirmLabel="Publish"
                variant="primary"
            />

            {(successMessage || errorMessage) && (
                <McModal
                    open={!!(successMessage || errorMessage)}
                    heading={successMessage ? "Success" : "Error"}
                    closed={() => {
                        setSuccessMessage(null);
                        setErrorMessage(null);
                    }}
                    dimension="small"
                >
                    <div className="mds-text--body-regular">
                        {successMessage || errorMessage}
                    </div>
                    <McButton
                        slot="primaryAction"
                        onClick={() => {
                            setSuccessMessage(null);
                            setErrorMessage(null);
                        }}
                    >
                        OK
                    </McButton>
                </McModal>
            )}

            <FloatingAssistant
                open={isTestChatOpen}
                onOpen={() => setIsTestChatOpen(true)}
                onClose={() => setIsTestChatOpen(false)}
                hideLauncher={true}
                chatHistory={testChatHistory}
                setChatHistory={setTestChatHistory}
                aiIntent={testAiIntent}
                setAiIntent={setTestAiIntent}
                aiDomainOverride=""
                setAiDomainOverride={() => { }}
                aiDomainOptions={[]}
                aiGenerateMutation={aiTestMutation}
                handleAiGenerate={handleAiTestSendMessage}
                onRetryMessage={handleAiTestRetryMessage}
                chatScrollRef={chatScrollRef}
                agentId={currentAgentId}
                agentName={agentName}
                selectedTerminal={selectedTerminal}
                setSelectedTerminal={setSelectedTerminal}
                databaseType={databaseType}
                setDatabaseType={setDatabaseType}
                testResponseType={testResponseType}
                setTestResponseType={setTestResponseType}
                testChatProfile={testChatProfile}
                aiModelName={(() => {
                    const ai = nodes.find((n) => n.type === 'aiAction');
                    return (ai?.data as Record<string, any>)?.model || DEFAULT_MODEL.value;
                })()}
                onNewChat={handleNewTestChat}
            />
        </div>
    );
};

export default AgentBuilder;
